import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Minus, ShoppingCart, CheckCircle2, AlertCircle, Loader2, UtensilsCrossed } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/**
 * In-room menu ordering, opened by a guest from their own link.
 *
 * The token in the URL is the guest's identity — it belongs to exactly one
 * booking, so an order can only ever be charged to that stay. Nothing here
 * asks who they are, because the link already answers it.
 *
 * Public and unauthenticated by design; every gate (POS feature flag, the
 * property's own guest-ordering toggle, and whether the stay is still open)
 * is enforced server-side in routes/api.js's /order/:token.
 */
const inr = n => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function GuestOrderPage() {
  const { token } = useParams();
  const [data, setData]       = useState(null);
  const [error, setError]     = useState("");
  const [qty, setQty]         = useState({});      // itemId -> count
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced]   = useState(null);    // { total }

  useEffect(() => {
    fetch(`${API}/order/${token}`)
      .then(r => r.json())
      .then(j => { if (j.success) setData(j.data); else setError(j.error || "This link isn't valid."); })
      .catch(() => setError("Couldn't load the menu. Please try again."));
  }, [token]);

  const cart = useMemo(
    () => (data?.items || [])
      .map(i => ({ ...i, qty: qty[i._id] || 0 }))
      .filter(i => i.qty > 0),
    [data, qty]
  );
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const bump = (id, by) => setQty(q => ({ ...q, [id]: Math.max(0, Math.min(50, (q[id] || 0) + by)) }));

  async function placeOrder() {
    setPlacing(true); setError("");
    try {
      const res = await fetch(`${API}/order/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: cart.map(i => ({ itemId: i._id, qty: i.qty })) }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Couldn't place the order.");
      setPlaced({ total: json.data.total });
      setQty({});
    } catch (err) {
      setError(err.message);
    } finally {
      setPlacing(false);
    }
  }

  if (error && !data) return <Shell><Center icon={<AlertCircle size={26} className="text-rose-500" />}
    title="Menu unavailable" sub={error} /></Shell>;

  if (!data) return <Shell><Center icon={<Loader2 size={22} className="text-amber-500 animate-spin" />} sub="Loading menu…" /></Shell>;

  if (placed) {
    return (
      <Shell>
        <Center
          icon={<CheckCircle2 size={30} className="text-emerald-500" />}
          title="Order placed"
          sub={`${inr(placed.total)} has been added to your room bill. We'll bring it up shortly.`}
        />
        <button onClick={() => setPlaced(null)}
          className="mt-6 w-full min-h-[48px] rounded-xl border border-stone-200 text-stone-700 font-medium hover:bg-stone-50">
          Order something else
        </button>
      </Shell>
    );
  }

  const byCategory = (data.items || []).reduce((acc, i) => {
    const c = i.category || "Menu";
    (acc[c] = acc[c] || []).push(i);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-lg mx-auto px-4 py-6 pb-40">
        <div className="mb-5">
          <p className="text-xl font-bold text-stone-900">{data.hotelName}</p>
          <p className="text-sm text-stone-500 mt-0.5">
            {data.guestName ? `${data.guestName} · ` : ""}{data.roomName || data.bookingRef}
          </p>
        </div>

        {!data.orderable && (
          <div className="mb-5 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3.5">
            <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">{data.reason}</p>
          </div>
        )}

        {data.items.length === 0 ? (
          <Center icon={<UtensilsCrossed size={26} className="text-stone-300" />} sub="No items on the menu yet." />
        ) : Object.entries(byCategory).map(([cat, items]) => (
          <div key={cat} className="mb-6">
            <p className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-2">{cat}</p>
            <div className="bg-white rounded-2xl border border-stone-200 divide-y divide-stone-100">
              {items.map(item => (
                <div key={item._id} className="flex items-center gap-3 p-3.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{item.name}</p>
                    <p className="text-sm text-stone-500">{inr(item.price)}</p>
                  </div>
                  {qty[item._id] ? (
                    <div className="flex items-center gap-1">
                      <button onClick={() => bump(item._id, -1)} disabled={!data.orderable}
                        aria-label={`Remove one ${item.name}`}
                        className="w-11 h-11 rounded-xl border border-stone-200 flex items-center justify-center text-stone-600 disabled:opacity-40">
                        <Minus size={15} />
                      </button>
                      <span className="w-7 text-center text-sm font-semibold text-stone-900">{qty[item._id]}</span>
                      <button onClick={() => bump(item._id, 1)} disabled={!data.orderable}
                        aria-label={`Add one ${item.name}`}
                        className="w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center disabled:opacity-40">
                        <Plus size={15} />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => bump(item._id, 1)} disabled={!data.orderable}
                      className="min-h-[44px] px-4 rounded-xl bg-amber-50 text-amber-700 text-sm font-semibold
                        hover:bg-amber-100 disabled:opacity-40 transition-colors">
                      Add
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {error && <p className="text-sm text-rose-600 mb-4">{error}</p>}
      </div>

      {/* Sticky order bar — clears the home-indicator area on phones. */}
      {cart.length > 0 && data.orderable && (
        <div className="fixed bottom-0 inset-x-0 bg-white/90 backdrop-blur-xl border-t border-stone-200 safe-bottom">
          <div className="max-w-lg mx-auto p-4">
            <button onClick={placeOrder} disabled={placing}
              className="w-full min-h-[52px] rounded-xl bg-gradient-to-r from-amber-500 to-orange-500
                text-white font-semibold shadow-sm hover:from-amber-600 hover:to-orange-600
                disabled:opacity-60 flex items-center justify-center gap-2 transition-all">
              <ShoppingCart size={17} />
              {placing ? "Placing…" : `Order · ${inr(total)}`}
            </button>
            <p className="text-[11px] text-stone-400 text-center mt-2">
              Charged to your room — payable at checkout.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl border border-stone-200 p-6">{children}</div>
    </div>
  );
}

function Center({ icon, title, sub }) {
  return (
    <div className="text-center">
      <div className="flex justify-center">{icon}</div>
      {title && <p className="mt-3 font-semibold text-stone-900">{title}</p>}
      {sub && <p className="mt-1 text-sm text-stone-500">{sub}</p>}
    </div>
  );
}
